import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import NapQrModal from "./NapQrModal";
import NapEditModal from "./NapEditModal";
import NapMergeQrModal from "./NapMergeQrModal";
import NapDetailsResultModal from "./NapDetailsResultModal";
import NapHistoryModal from "./NapHistoryModal";
import { useConfirm } from "../../context/ConfirmContext";

/**
 * NAP Table Modal — full-featured NAP Locator table within a modal overlay.
 * Used by NI Map to let users view/manage NAP codes without leaving the dashboard.
 *
 * Props:
 *  - lcpCode: string — initial search/filter (e.g. "PSG004 LP326")
 *  - onClose: () => void
 *  - onRecordUpdated: () => void — called after edits so caller can refresh
 */
export default function NapTableModal({ lcpCode, onClose, onRecordUpdated }) {
  const { hasPerm } = useAuth();
  const { confirm } = useConfirm();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(lcpCode || "");
  const [debouncedSearch, setDebouncedSearch] = useState(lcpCode || "");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState("nap_code");
  const [sortDir, setSortDir] = useState("asc");
  const [filterStat, setFilterStat] = useState("");
  const [stats, setStats] = useState({ available_ports: 0, full_nap: 0, no_stencil: 0, not_yet_updated: 0 });

  // Modals/actions state
  const [editRecord, setEditRecord] = useState(null);
  const [qrModal, setQrModal] = useState(null);
  const [generatingQrId, setGeneratingQrId] = useState(null);
  const [fetchingNapDetailsId, setFetchingNapDetailsId] = useState(null);
  const [napDetailsModal, setNapDetailsModal] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [mergeModal, setMergeModal] = useState(null);
  const [fetchingMergeNapDetails, setFetchingMergeNapDetails] = useState(false);

  const pageSize = 100;
  const canEdit = hasPerm("nap_locator.edit");
  const canDelete = hasPerm("nap_locator.delete");
  const canQrGenerate = hasPerm("nap_locator.qr_generate");
  const canQrDelete = hasPerm("nap_locator.qr_delete");
  const canMergeDetails = hasPerm("nap_locator.merge_details");
  const hasActions = canEdit || canDelete;

  const getLcp = (napCode) => {
    if (!napCode) return "";
    const parts = napCode.trim().split(/\s+/);
    return parts.length >= 2 ? `${parts[0]} ${parts[1]}` : napCode;
  };

  const selectedLcp = useMemo(() => {
    if (selectedIds.size === 0) return null;
    const firstSelectedRecord = records.find((r) => selectedIds.has(r.id));
    return firstSelectedRecord ? getLcp(firstSelectedRecord.nap_code) : null;
  }, [selectedIds, records]);

  // Group individual (non-merged) NAPs by lat/lon for visual grouping
  const locationGroupColors = useMemo(() => {
    const colors = [
      "border-l-blue-400 bg-blue-50/40",
      "border-l-green-400 bg-green-50/40",
      "border-l-purple-400 bg-purple-50/40",
      "border-l-orange-400 bg-orange-50/40",
      "border-l-pink-400 bg-pink-50/40",
      "border-l-cyan-400 bg-cyan-50/40",
    ];
    // Check if a NAP code is individual (single NP number, not merged like NP1-2)
    const isIndividual = (napCode) => {
      if (!napCode) return false;
      const match = napCode.match(/NP(\d+)$/i);
      return !!match;
    };
    // Group by lat/lon (rounded to 5 decimals)
    const groups = {};
    for (const r of records) {
      if (!isIndividual(r.nap_code) || r.latitude == null || r.longitude == null) continue;
      const key = `${Number(r.latitude).toFixed(5)}_${Number(r.longitude).toFixed(5)}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(r.id);
    }
    // Only color groups with 2+ records
    const colorMap = {};
    let colorIdx = 0;
    for (const [, ids] of Object.entries(groups)) {
      if (ids.length < 2) continue;
      const cls = colors[colorIdx % colors.length];
      for (const id of ids) colorMap[id] = cls;
      colorIdx++;
    }
    return colorMap;
  }, [records]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchRecords = useCallback(async () => {
    if (!debouncedSearch.trim()) {
      setRecords([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = { search: debouncedSearch, page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir, stat_filter: filterStat };
      const [listRes, countRes, statsRes] = await Promise.all([
        api.get("/nap-locator", { params }),
        api.get("/nap-locator/count", { params: { search: debouncedSearch, stat_filter: filterStat } }),
        api.get("/nap-locator/stats", { params: { search: debouncedSearch } }),
      ]);
      setRecords(listRes.data);
      setTotalCount(countRes.data.total);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Failed to fetch NAP records", err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, sortBy, sortDir, filterStat]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
    setPage(1);
  };

  const handleFilterStat = (key) => {
    setFilterStat((prev) => (prev === key ? "" : key));
    setPage(1);
  };

  const handleDelete = async (record) => {
    const ok = await confirm({
      title: "Delete NAP Record",
      message: `Are you sure you want to delete "${record.nap_code}"? This action cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/nap-locator/${record.id}`);
      fetchRecords();
      onRecordUpdated?.();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete record.");
    }
  };

  const handleGenerateQr = async (record) => {
    setGeneratingQrId(record.id);
    try {
      const { data } = await api.post(`/nap-locator/${record.id}/qr/preview`);
      setQrModal({ record, mode: "preview", previewData: data });
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to generate QR preview.");
    } finally {
      setGeneratingQrId(null);
    }
  };

  const handleNapDetails = async (record) => {
    setFetchingNapDetailsId(record.id);
    try {
      const { data } = await api.post(`/nap-locator/${record.id}/nap-details`);
      if (data.found) {
        setNapDetailsModal(data);
        fetchRecords();
        onRecordUpdated?.();
      } else {
        await confirm({
          title: "No NAP Details Found",
          message: data.detail || `No NAP details found for "${record.nap_code}" on the external server.`,
          confirmLabel: "OK",
          cancelLabel: "Close",
        });
        try {
          await api.post(`/nap-locator/${record.id}/no-stencil`);
          fetchRecords();
          onRecordUpdated?.();
        } catch (e) {}
      }
    } catch (err) {
      await confirm({
        title: "NAP Details Error",
        message: err.response?.data?.detail || "Failed to fetch NAP details from external server.",
        confirmLabel: "OK",
        cancelLabel: "Close",
        variant: "danger",
      });
    } finally {
      setFetchingNapDetailsId(null);
    }
  };

  const handleViewQr = (record) => {
    setQrModal({ record, mode: "view", previewData: null });
  };

  const handleMergeQrPreview = async () => {
    try {
      const ids = Array.from(selectedIds);
      const { data } = await api.post("/nap-locator/merge-qr/preview", { record_ids: ids });
      setMergeModal({ previewData: data });
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to generate merge QR preview.");
    }
  };

  const handleMergeNapDetails = async () => {
    setFetchingMergeNapDetails(true);
    try {
      const ids = Array.from(selectedIds);
      const { data } = await api.post("/nap-locator/merge-nap-details", { record_ids: ids });
      if (data.found) {
        setNapDetailsModal({ ...data, _isMerge: true, _recordIds: ids });
      } else {
        await confirm({
          title: "No NAP Details Found",
          message: data.detail || `No NAP details found for "${data.merged_nap_code}" on the external server.`,
          confirmLabel: "OK",
          cancelLabel: "Close",
        });
      }
    } catch (err) {
      await confirm({
        title: "NAP Details Error",
        message: err.response?.data?.detail || "Failed to fetch NAP details from external server.",
        confirmLabel: "OK",
        cancelLabel: "Close",
        variant: "danger",
      });
    } finally {
      setFetchingMergeNapDetails(false);
    }
  };

  const handleQrSaved = () => {
    setQrModal(null);
    fetchRecords();
    onRecordUpdated?.();
  };

  const handleEditSaved = () => {
    setEditRecord(null);
    fetchRecords();
    onRecordUpdated?.();
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const SortHeader = ({ column, children, className = "" }) => (
    <th
      className={`px-3 py-2 font-semibold cursor-pointer select-none hover:bg-gray-200 ${className}`}
      onClick={() => handleSort(column)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortBy === column && (
          <span className="text-blue-600">{sortDir === "asc" ? "▲" : "▼"}</span>
        )}
      </span>
    </th>
  );

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40">
      <div
        className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-[1400px] h-[90vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-gray-800">NAP Locator Table</h2>
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search NAP code or area..."
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <svg className="absolute left-2.5 top-2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {search && (
              <button onClick={() => setSearch("")} className="text-xs text-gray-500 hover:text-gray-700">Clear</button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Selection actions */}
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{selectedIds.size} selected</span>
                {canMergeDetails && (
                  <button
                    onClick={handleMergeNapDetails}
                    disabled={fetchingMergeNapDetails}
                    className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {fetchingMergeNapDetails ? "Fetching..." : "Merge NAP Details"}
                  </button>
                )}
                {canQrGenerate && (
                  <button
                    onClick={handleMergeQrPreview}
                    className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Merge QR
                  </button>
                )}
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-100"
                >
                  Clear
                </button>
              </div>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-500"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-2 px-5 py-2 border-b flex-shrink-0">
          <div
            onClick={() => stats.available_ports > 0 && handleFilterStat("available_ports")}
            className={`rounded px-3 py-1 text-center border text-xs cursor-pointer transition-all ${
              filterStat === "available_ports" ? "ring-2 ring-blue-600 border-blue-600" : "bg-green-50 border-green-200"
            }`}
          >
            <span className="font-semibold text-gray-700">Available NAP</span>
            <span className="ml-1.5 font-bold">{stats.available_ports}</span>
          </div>
          <div
            onClick={() => stats.full_nap > 0 && handleFilterStat("full_nap")}
            className={`rounded px-3 py-1 text-center border text-xs cursor-pointer transition-all ${
              filterStat === "full_nap" ? "ring-2 ring-blue-600 border-blue-600" : "bg-red-50 border-red-200"
            }`}
          >
            <span className="font-semibold text-gray-700">Full NAP</span>
            <span className="ml-1.5 font-bold">{stats.full_nap}</span>
          </div>
          <div
            onClick={() => stats.no_stencil > 0 && handleFilterStat("no_stencil")}
            className={`rounded px-3 py-1 text-center border text-xs cursor-pointer transition-all ${
              filterStat === "no_stencil" ? "ring-2 ring-blue-600 border-blue-600" : "bg-blue-50 border-blue-200"
            }`}
          >
            <span className="font-semibold text-gray-700">No Record</span>
            <span className="ml-1.5 font-bold">{stats.no_stencil}</span>
          </div>
          <div
            onClick={() => stats.not_yet_updated > 0 && handleFilterStat("not_yet_updated")}
            className={`rounded px-3 py-1 text-center border text-xs cursor-pointer transition-all ${
              filterStat === "not_yet_updated" ? "ring-2 ring-blue-600 border-blue-600" : "bg-yellow-50 border-yellow-200"
            }`}
          >
            <span className="font-semibold text-gray-700">Not Yet Updated</span>
            <span className="ml-1.5 font-bold">{stats.not_yet_updated}</span>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr className="text-gray-600 text-left">
                <th className="px-3 py-2 font-semibold">#</th>
                {canQrGenerate && <th className="px-2 py-2"></th>}
                <SortHeader column="nap_code">NAP Code</SortHeader>
                <SortHeader column="no_of_ports">No of Ports</SortHeader>
                <SortHeader column="latitude">Latitude</SortHeader>
                <SortHeader column="longitude">Longitude</SortHeader>
                <SortHeader column="area">Area</SortHeader>
                <SortHeader column="used_port">Used Port</SortHeader>
                <SortHeader column="vacant">Unused/Vacant Port</SortHeader>
                <SortHeader column="onu_connect">ONU Connect</SortHeader>
                <SortHeader column="stencil">With Record</SortHeader>
                <th className="px-3 py-2 font-semibold text-center">QR Code</th>
                {hasActions && <th className="px-3 py-2 font-semibold">Actions</th>}
                <SortHeader column="updated_at">Updated By</SortHeader>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={99} className="text-center py-8 text-gray-400">Loading...</td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={99} className="text-center py-8 text-gray-400">
                    {debouncedSearch.trim() ? "No NAP records found." : "Enter a search term to view records."}
                  </td>
                </tr>
              ) : (
                records.map((r, idx) => (
                  <tr key={r.id} className={`${locationGroupColors[r.id] ? `border-l-4 ${locationGroupColors[r.id]}` : (idx % 2 === 0 ? "bg-white" : "bg-gray-50")}`}>
                    <td className="px-3 py-2 text-gray-500">{(page - 1) * pageSize + idx + 1}</td>
                    {canQrGenerate && (
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(r.id)}
                          disabled={r.stencil === "Y" || (selectedLcp !== null && !selectedIds.has(r.id) && getLcp(r.nap_code) !== selectedLcp)}
                          onChange={(e) => {
                            const next = new Set(selectedIds);
                            if (e.target.checked) next.add(r.id);
                            else next.delete(r.id);
                            setSelectedIds(next);
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed"
                        />
                      </td>
                    )}
                    <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">{r.nap_code}</td>
                    <td className="px-3 py-2">{r.no_of_ports ?? "—"}</td>
                    <td className="px-3 py-2">{r.latitude ?? "—"}</td>
                    <td className="px-3 py-2">{r.longitude ?? "—"}</td>
                    <td className="px-3 py-2">{r.area ?? "—"}</td>
                    <td className="px-3 py-2">{r.used_port ?? "—"}</td>
                    <td className="px-3 py-2">{r.vacant ?? "—"}</td>
                    <td className="px-3 py-2">{r.onu_connect ?? "—"}</td>
                    <td className="px-3 py-2">{r.stencil ?? "—"}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {canQrGenerate && (
                          <button
                            onClick={() => handleNapDetails(r)}
                            disabled={fetchingNapDetailsId === r.id}
                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            {fetchingNapDetailsId === r.id ? "Fetching..." : "NAP Details"}
                          </button>
                        )}
                        {r.qr_code_data ? (
                          <img
                            src={`data:image/png;base64,${r.qr_code_data}`}
                            alt="QR"
                            className="w-10 h-10 inline-block cursor-pointer rounded border hover:opacity-80"
                            onClick={() => handleViewQr(r)}
                          />
                        ) : canQrGenerate ? (
                          <button
                            onClick={() => handleGenerateQr(r)}
                            disabled={generatingQrId === r.id}
                            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            {generatingQrId === r.id ? "..." : "Generate QR"}
                          </button>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </div>
                    </td>
                    {hasActions && (
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          {canEdit && (
                            <button
                              onClick={() => setEditRecord(r)}
                              className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600"
                            >
                              Edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(r)}
                              className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-3 py-2">
                      {r.updated_by_name ? (
                        <button
                          onClick={() => setHistoryModal({ record: r })}
                          className="text-blue-600 hover:underline text-xs"
                        >
                          {r.updated_by_name}
                        </button>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="flex items-center justify-between px-5 py-2 border-t bg-gray-50 flex-shrink-0 text-sm text-gray-600">
            <span>
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-100"
                >
                  Prev
                </button>
                <span>Page {page} of {totalPages}</span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-100"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sub-modals */}
      {qrModal && (
        <NapQrModal
          record={qrModal.record}
          mode={qrModal.mode}
          previewData={qrModal.previewData}
          onClose={() => setQrModal(null)}
          onSaved={handleQrSaved}
          canDeleteQr={canQrDelete}
          canEdit={canEdit}
        />
      )}
      {editRecord && (
        <NapEditModal
          record={editRecord}
          onClose={() => setEditRecord(null)}
          onSaved={handleEditSaved}
        />
      )}
      {mergeModal && (
        <NapMergeQrModal
          previewData={mergeModal.previewData}
          onClose={() => { setMergeModal(null); setSelectedIds(new Set()); }}
          onSaved={() => { setMergeModal(null); setSelectedIds(new Set()); fetchRecords(); onRecordUpdated?.(); }}
        />
      )}
      {napDetailsModal && (
        <NapDetailsResultModal
          data={napDetailsModal}
          onChanged={() => { fetchRecords(); onRecordUpdated?.(); }}
          onClose={() => { setNapDetailsModal(null); fetchRecords(); onRecordUpdated?.(); }}
          onMergeSave={napDetailsModal._isMerge ? async (modalData) => {
            await api.post("/nap-locator/merge-nap-details/save", {
              record_ids: modalData._recordIds,
              used_port: modalData.used_port,
              vacant: modalData.vacant,
              onu_connect: modalData.onu_connect,
              audit: modalData.audit || [],
              last_audit: modalData.last_audit ?? null,
              is_scan_completed: modalData.is_scan_completed ?? false,
            });
            setSelectedIds(new Set());
          } : undefined}
        />
      )}
      {historyModal && (
        <NapHistoryModal
          record={historyModal.record}
          onClose={() => setHistoryModal(null)}
        />
      )}

      {/* Loading overlay for NAP details fetch */}
      {(fetchingNapDetailsId || fetchingMergeNapDetails) && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20">
          <div className="relative flex items-center justify-center w-20 h-20">
            <svg
              className="absolute inset-0 w-full h-full animate-spin"
              style={{ animationDuration: "1.5s" }}
              viewBox="0 0 100 100"
              fill="none"
            >
              <polygon
                points="50,2 93,25 93,75 50,98 7,75 7,25"
                stroke="#2563eb"
                strokeWidth="4"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="12 6"
              />
            </svg>
            <img
              src="/best-icon.png"
              alt=""
              className="w-8 h-8 object-contain rounded"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
